import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { LoadingState, ErrorState, DemoDataNotice } from "@/components/ui/AsyncState";
import { ConversationStatusBadge } from "@/components/ui/StatusBadge";
import { useApi } from "@/lib/use-api";
import { formatPhone, formatDateTime } from "@/lib/format";
import type { ConversationWithPreview, Message } from "@platform/shared";

const SENDER_LABEL: Record<Message["senderType"], string> = {
  human: "Customer",
  ai: "Callback AI",
  system: "Automated",
};

export function ConversationDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useApi<{
    conversation: ConversationWithPreview;
    messages: Message[];
  }>(`/api/conversations/${id}`, [id]);

  if (isLoading) return <LoadingState label="Loading conversation…" />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;
  if (!data) return null;

  const { conversation, messages } = data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/dashboard/conversations" className="text-sm text-ink-soft hover:text-ink">
          ← Back to conversations
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl text-ink">
              {conversation.leadName ?? formatPhone(conversation.leadPhone)}
            </h1>
            <p className="mt-1 text-sm text-ink-soft">{formatPhone(conversation.leadPhone)}</p>
          </div>
          <ConversationStatusBadge status={conversation.status} />
        </div>
      </div>

      <Card className="p-5">
        {messages.length === 0 && <p className="text-sm text-ink-soft">No messages in this conversation yet.</p>}
        <div className="space-y-4">
          {messages.map((message) => {
            const fromCustomer = message.direction === "inbound";
            return (
              <div key={message.id} className={`flex ${fromCustomer ? "justify-start" : "justify-end"}`}>
                <div
                  className={`max-w-md rounded-lg px-4 py-2.5 text-sm ${
                    fromCustomer ? "bg-paper text-ink" : "bg-brand-700 text-white"
                  }`}
                >
                  <p>{message.body}</p>
                  <p className={`mt-1 text-[11px] ${fromCustomer ? "text-ink-soft" : "text-brand-100"}`}>
                    {SENDER_LABEL[message.senderType]} · {formatDateTime(message.sentAt)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <DemoDataNotice>
        This thread is read-only for now — sending a reply requires the SMS/AI system, which isn't built yet.
      </DemoDataNotice>
    </div>
  );
}
